<template>
  <article
    class="benefits-tier-card"
    :class="{ 'is-interactive': interactive }"
    :style="cardStyle"
    :role="interactive ? 'button' : null"
    :tabindex="interactive ? 0 : null"
    @click="handleClick"
    @keydown.enter.prevent="handleClick"
  >
    <div class="benefits-tier-card-main">
      <span
        v-if="sortable"
        class="benefits-tier-card-handle"
        :aria-label="t('benefitsCenter-tiers-dragHandle')"
        @click.stop
      >
        <span v-for="dot in 6" :key="dot"></span>
      </span>

      <span v-if="svgSource || emoji" class="benefits-tier-card-icon" aria-hidden="true">
        <img v-if="svgSource" :src="svgSource" alt="" />
        <template v-else>{{ emoji }}</template>
      </span>

      <span class="benefits-tier-card-content">
        <strong>{{ tier.name }}</strong>
        <div class="benefits-tier-card-description" v-html="descriptionHtml"></div>
      </span>
    </div>

    <div v-if="showBecoming" class="benefits-tier-card-becoming">
      <h5>{{ t("benefitsCenter-tiers-becomingTitle", { name: tier.name }) }}</h5>
      <div class="benefits-tier-card-becoming-text" v-html="becomingHtml"></div>
    </div>
  </article>
</template>

<script>
import { svgToDataUri } from "./tiers-utils";
import { sanitizeRichText } from "../../../helpers/html";

export default {
  name: "BenefitsTierCard",
  props: {
    tier: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
    sortable: { type: Boolean, default: false },
    showBecoming: { type: Boolean, default: false },
  },
  computed: {
    cardStyle() {
      return {
        "--tier-color": this.tier.color || "#b45f24",
        "--tier-text-color": this.tier.textColor || "#fcfcfd",
      };
    },
    svgSource() {
      return this.tier.iconType === "svg" ? svgToDataUri(this.tier.iconSvg) : "";
    },
    emoji() {
      return this.tier.iconType === "emoji" ? this.tier.iconEmoji : "";
    },
    descriptionHtml() {
      return sanitizeRichText(this.tier.description);
    },
    becomingHtml() {
      return sanitizeRichText(this.tier.becomingDescription);
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    handleClick() {
      if (this.interactive) {
        this.$emit("click", this.tier);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-tier-card {
  background: var(--tier-color);
  border-radius: 24px;
  color: var(--tier-text-color);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  text-align: left;
  width: 100%;

  &.is-interactive {
    cursor: pointer;
    transition: box-shadow 0.15s ease, transform 0.15s ease;

    &:hover {
      box-shadow: 0 8px 24px rgba(17, 25, 39, 0.14);
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      box-shadow: 0 0 0 3px rgba(42, 170, 76, 0.45);
    }
  }
}

.benefits-tier-card-main {
  align-items: center;
  display: flex;
  gap: 16px;
  min-height: 75px;
}

.benefits-tier-card-handle {
  cursor: grab;
  display: grid;
  flex: 0 0 14px;
  gap: 3px;
  grid-template-columns: repeat(2, 3px);
  justify-content: center;
  opacity: 0.7;

  span {
    background: var(--tier-text-color);
    border-radius: 50%;
    height: 3px;
    width: 3px;
  }

  &:active {
    cursor: grabbing;
  }
}

.benefits-tier-card-icon {
  align-items: center;
  display: inline-flex;
  flex: 0 0 64px;
  font-family: "Sora", sans-serif;
  font-size: 56px;
  height: 64px;
  justify-content: center;
  line-height: 1;

  img {
    display: block;
    height: 56px;
    object-fit: contain;
    width: 56px;
  }
}

.benefits-tier-card-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  strong {
    font-family: "Sora", sans-serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }
}

.benefits-tier-card-description {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.benefits-tier-card-becoming {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 8px 4px;
  position: relative;

  &::before {
    background: var(--tier-text-color);
    content: "";
    height: 1px;
    left: 0;
    opacity: 0.3;
    position: absolute;
    right: 0;
    top: 0;
  }

  h5 {
    color: inherit;
    font-family: "Inter", sans-serif;
    font-size: 24px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0;
  }
}

.benefits-tier-card-becoming-text {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.benefits-tier-card-description,
.benefits-tier-card-becoming-text {
  ::v-deep p {
    margin: 0;
  }

  ::v-deep ul,
  ::v-deep ol {
    margin: 0;
    padding-left: 20px;
  }
}

@media (max-width: 767px) {
  .benefits-tier-card {
    border-radius: 18px;
  }

  .benefits-tier-card-icon {
    flex-basis: 48px;
    font-size: 44px;
    height: 48px;

    img {
      height: 44px;
      width: 44px;
    }
  }

  .benefits-tier-card-content strong {
    font-size: 20px;
  }
}
</style>
